import { MockedSdk } from '../mockedSdk';
import { parseMessageEvent } from './parse-event.utils';
import {
  DEFAULT_GATEWAY_ID,
  DEFAULT_INIT_TIMEOUT,
  EMBED_PARAMETER_NAME,
  HQO_SDK_EVENT_NAME,
  HQO_SDK_EVENT_SUFFIX,
  INIT_TIMEOUT_MESSAGE,
  POST_MESSAGE_TARGET_ORIGIN,
  SECONDS_MULTIPLIER,
  WINDOW_POST_MESSAGE_EVENT_NAME,
} from './sdk.constants';
import { Card, DataPayload, SdkEventPayload } from './types/sdk-event.types';
import { SdkEvents, SdkHandlerPayload } from './types/sdk-handler.types';
import { HeaderWorkMode, HQO_SDK_ACTIONS, HqOSdkData } from './types/sdk-message.types';
import { Handler, HqoClientSdkInterface, SdkPropsInterface, ShowNativePaymentOptions } from './types/sdk.interface';
import { parseJson, response } from './utils';

export class HqoClientSdk implements HqoClientSdkInterface {
  private appUuid: string;

  private handlers: Partial<Record<SdkEvents, Array<Handler>>>;

  private developerMode: boolean;

  private initTimeout: number;

  private initComplete: boolean;

  constructor({ app_uuid, develop, init = true, init_timeout = 0 }: SdkPropsInterface) {
    this.appUuid = app_uuid;
    this.handlers = {};
    this.developerMode = develop;
    this.initTimeout = init_timeout * SECONDS_MULTIPLIER || DEFAULT_INIT_TIMEOUT;
    this.initComplete = false;

    if (window.addEventListener) {
      window.addEventListener(HQO_SDK_EVENT_NAME, this.emit, false);
      window.addEventListener(WINDOW_POST_MESSAGE_EVENT_NAME, this.handleMessage, false);
    } else {
      window.attachEvent(HQO_SDK_EVENT_NAME, this.emit);
      window.attachEvent(WINDOW_POST_MESSAGE_EVENT_NAME, this.handleMessage);
    }

    if (init) {
      this.init();
    }
  }

  getInstance() {
    return this.appUuid;
  }

  init() {
    this.postMessage(HQO_SDK_ACTIONS.INIT, { app_uuid: this.appUuid });

    setTimeout(() => {
      this.initConfirmation();
    }, this.initTimeout);
  }

  close() {
    this.postMessage(HQO_SDK_ACTIONS.CLOSE_WEB_VIEW);
  }

  logout() {
    this.postMessage(HQO_SDK_ACTIONS.LOGOUT);
  }

  private initConfirmation() {
    const fullUrl = new URL(window.location.href);
    const params = fullUrl.searchParams;
    const isEmbedVersion = params.get(EMBED_PARAMETER_NAME);

    if (!this.initComplete && isEmbedVersion) {
      params.delete(EMBED_PARAMETER_NAME);
      const redirectUrl = fullUrl.toString();
      this.broadcast(response(HQO_SDK_ACTIONS.INIT), { url_reload: redirectUrl, error: INIT_TIMEOUT_MESSAGE });
      window.location.replace(redirectUrl);
    }
  }

  identity = {
    getJWT: () => {
      this.postMessage(HQO_SDK_ACTIONS.GET_JWT);
    },
    getUser: () => {
      this.postMessage(HQO_SDK_ACTIONS.GET_USER);
    },
    getBuilding: () => {
      this.postMessage(HQO_SDK_ACTIONS.GET_BUILDING);
    },
    getBuildings: () => {
      this.postMessage(HQO_SDK_ACTIONS.GET_BUILDINGS);
    },
    getBrand: () => {
      this.postMessage(HQO_SDK_ACTIONS.GET_BRAND);
    },
    getTheme: () => {
      this.postMessage(HQO_SDK_ACTIONS.GET_THEME);
    },
  };

  host = {
    getInfo: () => {
      this.postMessage(HQO_SDK_ACTIONS.GET_HOST_INFO);
    },
  };

  header = {
    setHeader: (header_title?: string, work_mode?: HeaderWorkMode, header_url?: string, header_cta_icon?: string) => {
      this.postMessage(HQO_SDK_ACTIONS.SET_HEADER, { header_title, header_url, header_cta_icon, work_mode });
    },
    showHeader: () => {
      this.postMessage(HQO_SDK_ACTIONS.SHOW_HEADER);
    },
    hideHeader: () => {
      this.postMessage(HQO_SDK_ACTIONS.HIDE_HEADER);
    },
  };

  footer = {
    showFooter: () => {
      this.postMessage(HQO_SDK_ACTIONS.SHOW_FOOTER);
    },
    hideFooter: () => {
      this.postMessage(HQO_SDK_ACTIONS.HIDE_FOOTER);
    },
  };

  swipe = {
    enableBackSwipe: () => {
      this.postMessage(HQO_SDK_ACTIONS.ENABLE_BACK_SWIPE);
    },
    disableBackSwipe: () => {
      this.postMessage(HQO_SDK_ACTIONS.DISABLE_BACK_SWIPE);
    },
  };

  payments = {
    showNativePayment: (_type: unknown, config: ShowNativePaymentOptions, gatewayId = DEFAULT_GATEWAY_ID) => {
      this.postMessage(HQO_SDK_ACTIONS.SHOW_NATIVE_PAYMENT, {
        CURRENCY: config.currency,
        AUTHORIZATION_TOTAL: config.authorization_total,
        SUBTOTAL: config.subtotal,
        TAX: config.tax,
        TOTAL: config.total,
        payment_gateway_id: gatewayId,
      });
    },

    hideNativePayments: () => {
      this.postMessage(HQO_SDK_ACTIONS.HIDE_NATIVE_PAYMENTS);
    },
    getPaymentMethods: () => {
      this.postMessage(HQO_SDK_ACTIONS.GET_PAYMENT_METHODS);
    },
    tokenizeCard: (cardId: number | string, gatewayId = DEFAULT_GATEWAY_ID) => {
      this.postMessage(HQO_SDK_ACTIONS.TOKENIZE_CARD, {
        card_id: parseInt(cardId?.toString(), 10),
        gateway_id: gatewayId,
      });
    },

    showNativePaymentAuthorization: (type: unknown, config: ShowNativePaymentOptions) => {
      this.payments.showNativePayment(type, config);
    },
  };

  storage = {
    get: (key: string) => {
      this.postMessage(HQO_SDK_ACTIONS.GET_STORAGE, { key });
    },
    set: (key: string, value: unknown) => {
      this.postMessage(HQO_SDK_ACTIONS.SET_STORAGE, {
        key,
        value: typeof value !== 'string' ? JSON.stringify(value) : value,
      });
    },
  };

  on = <E extends SdkEvents>(eventName: E, handler: Handler<E>) => {
    if (!this.handlers[eventName]) {
      this.handlers[eventName] = [];
    }

    this.handlers[eventName]?.push(handler as Handler);
  };

  private postMessage<A extends keyof HqOSdkData>(action: A, data?: HqOSdkData[A]): void {
    this.postMessageToIos(action, data);
    this.postMessageToAndroid(action, data);
    this.postMessageToWeb(action, data);

    if (this.developerMode) {
      const mockedData = this.developerMocked(action);

      this.broadcast(`${action}${HQO_SDK_EVENT_SUFFIX}`, mockedData);
    }
  }

  private postMessageToIos<A extends keyof HqOSdkData>(action: A, data?: HqOSdkData[A]): void {
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers[action]) {
      window.webkit.messageHandlers[action].postMessage(data);
    }
  }

  private postMessageToAndroid<A extends keyof HqOSdkData>(action: A, data?: HqOSdkData[A]): void {
    try {
      if (typeof Android !== 'undefined' && Android?.[action]) {
        Android[action](JSON.stringify(data));
      }
    } catch (error) {
      if (error instanceof Error) {
        // eslint-disable-next-line no-console
        console.warn(`Could not post ${action} message: ${error.message}`);
      }
    }
  }

  private postMessageToWeb<A extends keyof HqOSdkData>(action: A, data?: HqOSdkData[A]): void {
    try {
      const messageData = JSON.stringify({ action, data });
      const isOpenedInIframe = window.self !== window.top;

      if (isOpenedInIframe) {
        window.parent?.postMessage(messageData, POST_MESSAGE_TARGET_ORIGIN);
      } else {
        window.top?.opener?.postMessage(messageData, POST_MESSAGE_TARGET_ORIGIN);
      }
    } catch (error) {
      if (error instanceof Error) {
        // eslint-disable-next-line no-console
        console.warn(`Could not post ${action} message: ${error.message}`);
      }
    }
  }

  private developerMocked = (action: HQO_SDK_ACTIONS) => {
    const mockedSdk = new MockedSdk();

    switch (action) {
      case HQO_SDK_ACTIONS.GET_JWT:
        return mockedSdk.getJWT();
      case HQO_SDK_ACTIONS.GET_USER:
        return mockedSdk.getUser();
      case HQO_SDK_ACTIONS.INIT:
        return mockedSdk.init();
      case HQO_SDK_ACTIONS.GET_HOST_INFO:
        return mockedSdk.getHostInfo();
      case HQO_SDK_ACTIONS.GET_BUILDING:
        return mockedSdk.getBuilding();
      case HQO_SDK_ACTIONS.GET_BUILDINGS:
        return mockedSdk.getBuildings();
      case HQO_SDK_ACTIONS.TOKENIZE_CARD:
        return mockedSdk.getTokenizeCard();
      case HQO_SDK_ACTIONS.GET_THEME:
        return mockedSdk.getTheme();
      case HQO_SDK_ACTIONS.GET_BRAND:
        return mockedSdk.getBrand();
      default:
        return new Error('Unknown action');
    }
  };

  private broadcast = (name = '', data = {}) => {
    setTimeout(() => {
      const myCustomEvent = new CustomEvent(HQO_SDK_EVENT_NAME, { detail: { event: name, data } });
      window.dispatchEvent(myCustomEvent);
    }, 0);
  };

  private emit = (event: CustomEventInit<SdkEventPayload<DataPayload>>) => {
    const { detail } = event;

    this.handleEvent(detail);
  };

  private handleMessage = (event: MessageEvent) => {
    const detail = parseMessageEvent(event);

    this.handleEvent(detail);
  };

  private handleEvent(eventPayload?: SdkEventPayload | null): void {
    let handlerPayload = eventPayload?.data as SdkHandlerPayload<SdkEvents>;

    if (eventPayload?.event === response(HQO_SDK_ACTIONS.INIT)) {
      this.initComplete = true;
    }

    if (eventPayload?.event === response(HQO_SDK_ACTIONS.GET_STORAGE) && typeof eventPayload?.data === 'string') {
      const { key, value } = JSON.parse(eventPayload.data);
      handlerPayload = { [key]: value };
    }

    if (
      eventPayload?.event === response(HQO_SDK_ACTIONS.GET_PAYMENT_METHODS) &&
      typeof eventPayload?.data === 'string'
    ) {
      const { cards } = JSON.parse(eventPayload.data);
      handlerPayload = {
        apple_pay: false,
        google_pay: false,
        cards: cards.map((card: Card) => ({
          card_last_four: card.last_four,
          card_brand: card.card_type,
          token_id: card.id,
          token: card.token,
        })),
      };
    }

    if (typeof handlerPayload === 'string') {
      handlerPayload = parseJson(handlerPayload) as SdkHandlerPayload<SdkEvents>;
    }

    if (eventPayload) {
      this.handlers[eventPayload.event]?.forEach((handler) => handler?.(handlerPayload));
    }
  }
}
